import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, reactive, toRef } from "vue";

import { Cancellation } from "@/api";
import { useSingleton } from "@/composables";
import { DictionariesHelper, EntitiesHelper, StoresHelper, TagsHelper } from "@/helpers";
import { DictionariesService } from "@/services";
import type { Dictionary } from "@/types";

interface State {
  status: "initial" | "loading" | "loaded" | "failed";
  dictionaries: Dictionary[];
}

const getDictionariesSingleton = useSingleton(DictionariesService.getOwnDictionaries);

export const useDictionariesStore = defineStore(
  "dictionaries",
  () => {
    const state = reactive<State>({
      status: "initial",
      dictionaries: []
    });

    const dictionaries = computed(() => state.dictionaries.filter((dictionary) => !dictionary.isUpsell));
    const upsellDictionaries = computed(() => state.dictionaries.filter((dictionary) => dictionary.isUpsell));

    const companyDictionaries = computed(() => TagsHelper.filterByTags(state.dictionaries, ["company"]));
    const companyDictionaryGroupHeading = computed<string>(() => companyDictionaries.value[0]?.groupHeading ?? "");

    const dictionaryIds = computed(() => EntitiesHelper.getIds(state.dictionaries));

    const methods = computed(() => DictionariesHelper.getMethods(state.dictionaries));
    const methodIds = computed(() => EntitiesHelper.getIds(methods.value));

    const fetch = async () => {
      const prevStatus = state.status;

      try {
        state.status = "loading";
        state.dictionaries = await getDictionariesSingleton();
        state.status = "loaded";
      } catch (e) {
        if (e instanceof Cancellation) {
          state.status = prevStatus;
          return;
        }

        state.status = "failed";
        throw e;
      }
    };

    const getDictionaryById = (dictionaryId: string) =>
      state.dictionaries.find((dictionary) => dictionary.id === dictionaryId);

    const getDictionaryByMethodId = (methodId: string) =>
      state.dictionaries.find((dictionary) => dictionary.methods.some((method) => method.id === methodId));

    const getMethodByMethodId = (methodId: string) => {
      const dictionary = getDictionaryByMethodId(methodId);
      if (!dictionary) {
        return undefined;
      }

      return dictionary.methods.find((method) => method.id === methodId);
    };

    return {
      status: toRef(state, "status"),
      fetch,
      getDictionaryById,
      getDictionaryByMethodId,
      getMethodByMethodId,
      dictionaries,
      upsellDictionaries,
      companyDictionaries,
      companyDictionaryGroupHeading,
      dictionaryIds,
      methods,
      methodIds
    };
  },
  {
    persist: {
      pick: StoresHelper.validatePaths<State>(["dictionaries"])
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDictionariesStore, import.meta.hot));
}
